import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BreadCrum from "../../../../common/BreadCrum/BreadCrum";
import CommonTable from "../../../../common/table/ComonTable";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import apiServices from "../../../../../services/apiService";
import storageService from "../../../../../utils/storageService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAdd, faMap } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";

const TABLE_HEADERS = [
    { key: "SlotNumber", label: "SLOT NO." },
    { key: "ParkingTypeName", label: "PARKING TYPE" },
    { key: "AssignedUserName", label: "ASSIGNED USER" },
    { key: "VehicleNumber", label: "VEHICLE NO." },
    { key: "Status", label: "STATUS" }
];

const AssignedParkingList = ({ onSwitchToGrid }) => {
    const navigate = useNavigate();
    const [assignments, setAssignments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { user: authUser } = useSelector((state) => state.auth);
    const roleId = Number(authUser?.roleId || authUser?.RoleId || storageService.getItem("roleId"));


    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 0,
        totalPages: 1
    });

    const breadcrumbs = [
        { label: "Parking Management", link: "/zentcomm/parking-management/assigned-slots" },
        { label: "Assigned Slots" }
    ];

    const fetchAssignments = async () => {
        setIsLoading(true);
        try {
            const siteId = authUser?.siteId || storageService.getItem("siteId");
            const unitId = authUser?.unitId || authUser?.branchorunitID;
            const isUnitAdmin = roleId === 4 || roleId === 6;
            const safeUrl = isUnitAdmin ? `/ParkingAssignment?unitId=${unitId}` : `/ParkingAssignment?siteId=${siteId}`;

            const response = await apiServices({
                method: "GET",
                url: safeUrl
            });

            const responseData = response?.data?.Data || response?.Data;
            const isSuccess = response?.data?.isSuccess === true || response?.data?.IsSuccess === true;

            if (isSuccess && Array.isArray(responseData)) {
                setAssignments(responseData);
                setPagination(prev => ({
                    ...prev,
                    totalRecords: responseData.length,
                    totalPages: Math.ceil(responseData.length / prev.pageSize)
                }));
            } else {
                setAssignments([]);
            }
        } catch (error) {
            console.error("Failed to fetch parking assignments", error);
            setAssignments([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAssignments();
    }, [authUser?.siteId, authUser?.unitId, authUser?.branchorunitID, roleId]);

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, pageNumber: page }));
    };

    const handleLimitChange = (e) => {
        setPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value), pageNumber: 1 }));
    };

    const formatDate = (dateStr) => {
        if (!dateStr || dateStr.includes("0001-01-01")) return "—";
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const renderCell = (key, row) => {
        if (key === "SlotNumber") {
            return <span className="text-xs font-bold text-gray-800 dark:text-textprimary uppercase">{row[key] || "—"}</span>;
        }
        if (key === "AssignedUserName") {
            return <span className="text-[13px] text-blue-600 dark:text-blue-400 font-semibold">{row[key] || "—"}</span>;
        }
        if (key === "Status") {
            const status = (row[key] || "").toLowerCase();
            let bgColor = "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";

            if (status === "occupied" || status === "allocated") {
                bgColor = "bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400 border border-orange-200 dark:border-orange-900/50";
            } else if (status === "available") {
                bgColor = "bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400 border border-green-200 dark:border-green-900/50";
            }

            return (
                <span className={`px-2.5 py-1 text-[10px] font-bold rounded-md uppercase tracking-wider ${bgColor}`}>
                    {row[key] || "Unknown"}
                </span>
            );
        }
        return <span className="text-[13px] text-gray-700 dark:text-textsecondary">{row[key] || "—"}</span>;
    };

    const expandedRowRender = (row) => {
        return (
            <div className="p-4 bg-gray-50/80 dark:bg-dashboardbg/50 border-t border-gray-100 dark:border-bordercolor rounded-b-lg">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex flex-col gap-1">
                        <span className="text-[10px] text-gray-500 dark:text-textsecondary uppercase font-bold tracking-wider">Mobile No.</span>
                        <span className="text-[13px] text-gray-800 dark:text-textprimary font-medium">{row.MobileNumber || "—"}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-[10px] text-gray-500 dark:text-textsecondary uppercase font-bold tracking-wider">Vehicle Type</span>
                        <span className="text-[13px] text-gray-800 dark:text-textprimary font-medium">{row.VehicleType || "—"}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-[10px] text-gray-500 dark:text-textsecondary uppercase font-bold tracking-wider">Start Date</span>
                        <span className="text-[13px] text-gray-800 dark:text-textprimary font-medium">{formatDate(row.StartDate)}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-[10px] text-gray-500 dark:text-textsecondary uppercase font-bold tracking-wider">End Date</span>
                        <span className="text-[13px] text-gray-800 dark:text-textprimary font-medium">{formatDate(row.EndDate)}</span>
                    </div>
                </div>
            </div>
        );
    };

    // Apply client-side pagination since API doesn't seem to paginate
    const paginatedData = assignments.slice(
        (pagination.pageNumber - 1) * pagination.pageSize,
        pagination.pageNumber * pagination.pageSize
    );

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-4 pt-4">
            <BreadCrum breadcrumbs={breadcrumbs} />
            <PageHeadingSection
                mainHeading="Assigned Parking Slots"
                subHeading={(roleId === 4 || roleId === 6) ? "View and manage parking assignments for your unit." : "View and manage all parking assignments across the site."}
                primaryBtnText={(roleId === 4 || roleId === 6) ? "Assign Slot" : "Visual Floor Layout"}
                primaryBtnIcon={(roleId === 4 || roleId === 6) ? faAdd : faMap}
                PrimaryOnClick={onSwitchToGrid}
            />

            <div className="shrink-0 flex-1 flex flex-col">
                <CommonTable
                    tableHeading="Assignment Directory"
                    headers={TABLE_HEADERS}
                    data={paginatedData}
                    renderCell={renderCell}
                    isLoading={isLoading}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    isRowExpandable={() => true}
                    expandedRowRender={expandedRowRender}
                />
            </div>
        </div>
    );
};

export default AssignedParkingList;
